import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Drawer, HStack, Portal, Text } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuMaximize2, LuMinimize2 } from 'react-icons/lu';
import { DURATION_FORM_REVEAL_OPEN_MS, EASE_OUT } from '@/components/ui/motion.ts';
import DrawerCloseButton from '@/components/ui/drawer-close-button.tsx';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useKeyboardInset } from '@/hooks/ui/useKeyboardInset.ts';
import { formatAddress } from '@/utils/addressUtils.ts';
import ComposerTabStrip from './ComposerTabStrip.tsx';
import ComposerForms from './ComposerForms.tsx';
import { composerSubmitKeyDown } from './composerSubmitShortcut.ts';
import ComposerActionRow from './ComposerActionRow.tsx';
import { useMeasuredHeight } from './useMeasuredHeight.ts';
import { COMPOSER_ADD_SHEET_HEIGHT, COMPOSER_ADD_SHEET_TRANSITION, ComposerTab } from './composerConstants.ts';
import { RecordComposerController } from './useRecordComposer.ts';
import { TaskComposerController } from './useTaskComposer.ts';

const SHEET_HANDLE_WIDTH = '40px';
const SHEET_HANDLE_HEIGHT = '4px';

interface ComposerAddSheetProps {
	isOpen: boolean;
	onClose: () => void;
	activeTab: ComposerTab;
	onSelectTab: (tab: ComposerTab) => void;
	record: RecordComposerController;
	task: TaskComposerController;
	isRecordTab: boolean;
	isTaskTab?: boolean;
	submitLabel: string;
	canSubmit: boolean;
	isSubmitting: boolean;
	onSubmit: () => void;
	tabs?: readonly ComposerTab[];
	dirtyTabs?: readonly ComposerTab[];
	onClearDraft?: () => void;
	additionalTab?: {
		tab: ComposerTab;
		form: React.ReactNode;
		/** The form has grown past what the compact sheet shows, as a sub-task or repeat does. */
		wantsRoom?: boolean;
	};
}

/**
 * Mobile add surface — a bottom sheet (auto-height) holding the same tab strip,
 * forms and action row as the desktop inline composer. Keeps the `+` from
 * squeezing the form's width on narrow screens.
 */
const ComposerAddSheet: React.FC<ComposerAddSheetProps> = (props) => {
	const {
		isOpen,
		onClose,
		activeTab,
		onSelectTab,
		record,
		task,
		isRecordTab,
		isTaskTab,
		submitLabel,
		canSubmit,
		isSubmitting,
		onSubmit,
		tabs,
		dirtyTabs,
		onClearDraft,
		additionalTab,
	} = props;

	const { house } = useCurrentHouse();
	const address = formatAddress(house.address).firstPart;
	const heading = address ? `Add to ${address}` : 'Add to property';

	const { ref: formsRef, height: formsHeight } = useMeasuredHeight();

	// The sheet auto-focuses the note/title field on open, which pops the keyboard immediately. The
	// drawer is bottom-anchored + fixed, so on mobile web it would sit behind the keyboard — reserve
	// the keyboard's height at the bottom so the footer (submit) stays above it.
	const keyboardInset = useKeyboardInset();

	// Don't animate the very first measured size (it would fight the drawer's
	// slide-in); enable the height transition a frame after opening so only
	// subsequent tab switches ease.
	const [animateResize, setAnimateResize] = useState(false);
	const [isSheetExpanded, setIsSheetExpanded] = useState(false);
	const sheetHeight = isSheetExpanded ? COMPOSER_ADD_SHEET_HEIGHT.Expanded : COMPOSER_ADD_SHEET_HEIGHT.Compact;
	const sheetBg = isRecordTab && record.isPrivate ? 'surface.subtle' : 'surface.default';

	/**
	 * A form that grows asks for room, and gets it only if it needs it.
	 *
	 * A sub-task, a repeat, or the contact fields opening out each used to expand the sheet outright,
	 * which on a tall phone took the sheet to full height for a row that would have fit. The ask
	 * watches the body's own scroll for a window of frames and expands the sheet once the content
	 * (forms plus the action row, where a repeat's frequency row lives) overflows the compact body,
	 * which is the moment the submit would be below the fold. Frames rather than one check because
	 * the growth is animated, and the first frame lands a few pixels in; a reopen over a draft that
	 * is already tall is answered on the first frame. Read straight off the DOM: a state per
	 * measurement would re-render the whole sheet on every frame of the reveal.
	 */
	const bodyRef = useRef<HTMLDivElement>(null);
	const roomFrameRef = useRef<number | null>(null);
	const requestRoom = () => {
		const until = performance.now() + DURATION_FORM_REVEAL_OPEN_MS * 2;
		if (roomFrameRef.current !== null) cancelAnimationFrame(roomFrameRef.current);
		const check = () => {
			roomFrameRef.current = null;
			const body = bodyRef.current;
			if (body && body.scrollHeight > body.clientHeight) {
				setIsSheetExpanded(true);
				return;
			}
			if (performance.now() < until) roomFrameRef.current = requestAnimationFrame(check);
		};
		roomFrameRef.current = requestAnimationFrame(check);
	};
	useEffect(
		() => () => {
			if (roomFrameRef.current !== null) cancelAnimationFrame(roomFrameRef.current);
		},
		[],
	);

	const addTaskChild = () => {
		requestRoom();
		task.addChild();
	};
	const toggleTaskRecurring = () => {
		if (!task.isRecurring) requestRoom();
		task.toggleRecurring();
	};

	useEffect(() => {
		if (!isOpen) {
			setAnimateResize(false);
			setIsSheetExpanded(false);
			return;
		}
		const id = requestAnimationFrame(() => setAnimateResize(true));
		return () => cancelAnimationFrame(id);
	}, [isOpen]);
	// The page's own tab asks for room the way a sub-task does: once, when its form opens out.
	const additionalTabWantsRoom = Boolean(additionalTab?.wantsRoom) && activeTab === additionalTab?.tab;
	useEffect(() => {
		if (isOpen && additionalTabWantsRoom) requestRoom();
	}, [additionalTabWantsRoom, isOpen]);

	return (
		<Drawer.Root
			placement="bottom"
			open={isOpen}
			closeOnInteractOutside={false}
			onOpenChange={(details) => {
				if (!details.open) onClose();
			}}
		>
			<Portal>
				<Drawer.Backdrop />
				<Drawer.Positioner>
					<Drawer.Content
						// The whole sheet, not just the body: the due date, the repeat toggle and the submit
						// live in the footer, and a chord that stops working depending on which control has
						// focus is worse than the tab strip above also answering to it.
						onKeyDown={composerSubmitKeyDown(canSubmit, onSubmit)}
						borderTopRadius="3xl"
						boxShadow="lg"
						bg={sheetBg}
						maxH={COMPOSER_ADD_SHEET_HEIGHT.Expanded}
						h={sheetHeight}
						overflow="hidden"
						display="flex"
						flexDirection="column"
						transition={COMPOSER_ADD_SHEET_TRANSITION}
						// Reserve the keyboard's height at the bottom (border-box, so it eats into the sheet
						// rather than growing it) — keeps the footer above the keyboard on mobile web.
						style={{ paddingBottom: keyboardInset ? `${keyboardInset}px` : undefined }}
						css={{
							'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
						}}
					>
						<Drawer.Header bg={sheetBg} borderTopRadius="3xl" display="block" pt={2} pb={0}>
							<Box display="flex" justifyContent="center" mb={2}>
								{/* A pointer affordance only. The labelled toggle beside the close button is the one
								    control assistive tech sees; two names for one action was two tab stops. */}
								<Button
									aria-hidden="true"
									tabIndex={-1}
									variant="plain"
									minW="auto"
									h={5}
									px={6}
									py={0}
									onClick={() => setIsSheetExpanded((expanded) => !expanded)}
									_hover={{ bg: 'transparent' }}
									_active={{ bg: 'transparent' }}
								>
									<Box
										aria-hidden="true"
										w={SHEET_HANDLE_WIDTH}
										h={SHEET_HANDLE_HEIGHT}
										borderRadius="full"
										bg="border.default"
									/>
								</Button>
							</Box>
							<HStack w="full" justify="space-between" align="center" gap={3}>
								<Text textStyle="body" fontWeight="semibold" flex={1} minW={0} truncate>
									{heading}
								</Text>
								<HStack gap={1} flexShrink={0}>
									{/* Clear lives up here rather than in the tab strip: at 375 three tabs plus the
									    strip's icons already fill the row, and a word would push the tabs under them. */}
									{onClearDraft && dirtyTabs?.includes(activeTab) && (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											h={8}
											px={3}
											rounded="full"
											color="fg.muted"
											textStyle="label.sm"
											onClick={onClearDraft}
										>
											Clear
										</Button>
									)}
									<IconButton
										type="button"
										aria-label={isSheetExpanded ? 'Collapse composer' : 'Expand composer'}
										aria-expanded={isSheetExpanded}
										variant="ghost"
										size="sm"
										rounded="full"
										color="fg.muted"
										_hover={{ bg: 'bg.emphasized' }}
										onClick={() => setIsSheetExpanded((expanded) => !expanded)}
									>
										{isSheetExpanded ? <LuMinimize2 /> : <LuMaximize2 />}
									</IconButton>
									{/* In the row, not the recipe's corner, so it sits beside the expand toggle. */}
									<DrawerCloseButton position="static" />
								</HStack>
							</HStack>
						</Drawer.Header>

						<Box flexShrink={0} bg={sheetBg}>
							<ComposerTabStrip
								activeTab={activeTab}
								onSelect={onSelectTab}
								showLabel={false}
								tabs={tabs}
								dirtyTabs={dirtyTabs}
							/>
						</Box>

						{/* One scroll container. The action row sits at the foot of the sheet while the form is
						    short, and is pushed down to be scrolled to once the form outgrows it: there is no
						    pinned footer for a body to scroll behind. */}
						<Drawer.Body
							ref={bodyRef}
							px={0}
							pt={0}
							pb={0}
							overflowY="auto"
							flex={1}
							minH={0}
							display="flex"
							flexDirection="column"
						>
							<Box
								flexShrink={0}
								overflow="hidden"
								style={{ height: formsHeight }}
								css={{
									transition: animateResize
										? `height ${DURATION_FORM_REVEAL_OPEN_MS}ms ${EASE_OUT}`
										: 'none',
									'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
								}}
							>
								<Box ref={formsRef}>
									<ComposerForms
										activeTab={activeTab}
										record={record}
										task={task}
										isOpen={isOpen}
										additionalTab={additionalTab}
									/>
								</Box>
							</Box>
							<Box mt="auto" flexShrink={0} borderTopWidth="1px" borderColor="border.subtle">
								<ComposerActionRow
									isRecordTab={isRecordTab}
									isTaskTab={isTaskTab}
									record={record}
									task={task}
									submitLabel={submitLabel}
									canSubmit={canSubmit}
									isSubmitting={isSubmitting}
									onSubmit={onSubmit}
									onAddTaskChild={addTaskChild}
									onToggleTaskRecurring={toggleTaskRecurring}
								/>
							</Box>
						</Drawer.Body>
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};

export default ComposerAddSheet;
